// client/src/components/secure/BusinessPicker.js — picking a business from the Business Library.
//
// Every place that asks "which business / use?" reads the catalog (GET /api/zoning-library/businesses,
// lib/businessCatalog.js): grouped by industry, searchable, synonyms and code terms match ("barbershop"
// finds Salon / barber, "trade school" finds Vocational school). Businesses with no Zoning Library rows
// yet are listed too, marked "not researched yet".
//
//   useBusinesses()                         the catalog (cached for the session; refresh() reloads)
//   BusinessPicker      value / onChange    one business (a name)
//   BusinessMultiPicker value[] / onChange  several (chips), the first is the main one
//   ResearchNowButton   business, towns     queues "Research this business" for those towns
//   NotResearched       gaps [{town, use_category, queued}]  "Not researched — Research now"
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const API = '/api/zoning-library';
let cache = null;
let inflight = null;
const listeners = new Set();
const lc = (s) => String(s || '').trim().toLowerCase();

/** The catalog: { businesses, groups, uses, can_manage }. */
export const useBusinesses = () => {
    const apiClient = useApiClient();
    const [data, setData] = useState(cache);
    const load = useCallback(async (force = false) => {
        if (cache && !force) { setData(cache); return cache; }
        if (!inflight || force) {
            inflight = apiClient.get(`${API}/businesses`).then((d) => { cache = d; listeners.forEach((f) => f(d)); return d; })
                .catch(() => ({ businesses: [], groups: [], uses: [], can_manage: false }))
                .finally(() => { inflight = null; });
        }
        const d = await inflight;
        setData(d);
        return d;
    }, [apiClient]);
    useEffect(() => {
        listeners.add(setData);
        load();
        return () => { listeners.delete(setData); };
    }, [load]);
    return { data, refresh: () => load(true) };
};

export const businessMatches = (b, q) => {
    const words = lc(q).split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const hay = [b.name, b.industry_group, ...(b.synonyms || []), ...(b.code_terms || [])].map(lc).join(' | ');
    return words.every((w) => hay.includes(w));
};

const Tag = ({ b }) => {
    if (b.legacy) return <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] text-gray-500">legacy</span>;
    if (!b.researched) return <span className="ml-1 rounded bg-red-50 px-1 text-[10px] text-red-700">not researched yet</span>;
    return <span className="ml-1 text-[10px] text-gray-400">{b.researched_towns || 0} town{b.researched_towns === 1 ? '' : 's'}</span>;
};

/**
 * One business. value: the business name. exclude: names not to offer (already chosen).
 */
export const BusinessPicker = ({ value, onChange, placeholder = 'Find a business — e.g. daycare, salon, warehouse', exclude = [], id, required = false, compact = false }) => {
    const { data } = useBusinesses();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const box = useRef(null);
    useEffect(() => {
        const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);
    const groups = useMemo(() => {
        const ex = new Set((exclude || []).map(lc));
        return ((data && data.groups) || []).map((g) => ({ group: g.group, businesses: g.businesses.filter((b) => !ex.has(lc(b.name)) && businessMatches(b, q)) }))
            .filter((g) => g.businesses.length);
    }, [data, q, exclude]);
    const pick = (name) => { onChange(name); setOpen(false); setQ(''); };
    const known = !value || ((data && data.businesses) || []).some((b) => lc(b.name) === lc(value));
    return (
        <div ref={box} className="relative">
            <button type="button" id={id} onClick={() => setOpen(!open)}
                className={`flex w-full items-center justify-between rounded border border-gray-300 bg-white px-2 ${compact ? 'py-1 text-xs' : 'py-1.5 text-sm'} text-left`}>
                <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || 'Pick a business…'}{!known && <span className="ml-1 text-[10px] text-amber-700">(not in the catalog)</span>}</span>
                <span className="text-gray-400">▾</span>
            </button>
            {required && <input tabIndex={-1} aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-0" required value={value || ''} onChange={() => {}} />}
            {open && (
                <div className="absolute z-30 mt-1 w-full min-w-[18rem] rounded border border-gray-300 bg-white shadow-lg">
                    <input autoFocus type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
                        className="w-full border-b border-gray-200 px-2 py-1.5 text-sm outline-none" aria-label="Find a business" />
                    <div className="max-h-72 overflow-y-auto py-1">
                        {!data && <p className="px-2 py-1 text-xs text-gray-500">Loading…</p>}
                        {data && !groups.length && <p className="px-2 py-1 text-xs text-gray-500">No business matches. The managing broker or office manager can add it under Zoning Library → Businesses.</p>}
                        {groups.map((g) => (
                            <div key={g.group}>
                                <p className="sticky top-0 bg-gray-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">{g.group}</p>
                                {g.businesses.map((b) => (
                                    <button type="button" key={b.name} onClick={() => pick(b.name)}
                                        className={`block w-full px-3 py-1 text-left text-sm hover:bg-indigo-50 ${lc(b.name) === lc(value) ? 'bg-indigo-50 font-semibold' : ''}`}>
                                        {b.name}<Tag b={b} />
                                        {q && b.synonyms && b.synonyms.some((s) => lc(s).includes(lc(q))) && !lc(b.name).includes(lc(q))
                                            && <span className="block text-[10px] text-gray-500">also: {b.synonyms.filter((s) => lc(s).includes(lc(q))).join(', ')}</span>}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Several businesses as chips; the first is the main one. */
export const BusinessMultiPicker = ({ value = [], onChange, label = 'Businesses', hint }) => {
    const remove = (n) => onChange(value.filter((v) => lc(v) !== lc(n)));
    return (
        <div>
            {label && <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</p>}
            {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
            <div className="mt-1 flex flex-wrap gap-1">
                {value.map((v, i) => (
                    <span key={v} className={`rounded-full border px-2 py-0.5 text-xs ${i === 0 ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'}`}>
                        {v}{i === 0 ? ' (main)' : ''}
                        <button type="button" onClick={() => remove(v)} className="ml-1 opacity-70 hover:opacity-100" aria-label={`Remove ${v}`}>×</button>
                    </span>
                ))}
                {!value.length && <span className="text-xs text-gray-400">None yet.</span>}
            </div>
            <div className="mt-1">
                <BusinessPicker value="" exclude={value} onChange={(n) => n && onChange([...value, n])} placeholder="Add a business — type any word" />
            </div>
        </div>
    );
};

/** Queue research of a business in some towns. */
export const ResearchNowButton = ({ business, towns, origin = 'library', searchId = null, label, onDone, small = true, disabled = false }) => {
    const apiClient = useApiClient();
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const list = (towns || []).filter(Boolean);
    const go = async () => {
        setBusy(true);
        try {
            const r = await apiClient.post(`${API}/research`, { business, towns: list, origin, search_id: searchId });
            setDone(true);
            alert(r.message);
            if (onDone) onDone(r);
        } catch (e) { alert(e.message || 'Could not queue the research.'); } finally { setBusy(false); }
    };
    if (done) return <span className="text-[11px] font-semibold text-indigo-700">Research queued</span>;
    return (
        <button type="button" disabled={busy || disabled || !business || !list.length} onClick={go}
            className={`rounded bg-indigo-700 font-semibold text-white disabled:opacity-50 ${small ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1.5 text-sm'}`}>
            {busy ? 'Queuing…' : (label || `Research now${list.length > 1 ? ` (${list.length} towns)` : ''}`)}
        </button>
    );
};

/** "Not researched — Research now", grouped by business. */
export const NotResearched = ({ gaps, origin, searchId = null, onDone }) => {
    const byUse = useMemo(() => {
        const m = new Map();
        for (const g of gaps || []) {
            if (!m.has(g.use_category)) m.set(g.use_category, { queued: [], open: [] });
            m.get(g.use_category)[g.queued ? 'queued' : 'open'].push(g.town);
        }
        return [...m.entries()];
    }, [gaps]);
    if (!byUse.length) return null;
    return (
        <div className="rounded border border-red-200 bg-red-50/60 p-2 text-xs">
            <p className="font-semibold text-red-800">Not researched yet in the Zoning Library</p>
            <ul className="mt-1 space-y-1">
                {byUse.map(([use, g]) => (
                    <li key={use} className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-gray-900">{use}:</span>
                        {g.open.length > 0 && <span className="text-gray-700">{g.open.join(', ')}</span>}
                        {g.open.length > 0 && <ResearchNowButton business={use} towns={g.open} origin={origin} searchId={searchId} onDone={onDone} />}
                        {g.queued.length > 0 && <span className="text-indigo-700">research queued: {g.queued.join(', ')}</span>}
                    </li>
                ))}
            </ul>
            <p className="mt-1 text-[11px] text-gray-500">The office computer researches the town's code (about every 30 minutes while it is on). Answers arrive as "AI draft — confirm" for the managing broker.</p>
        </div>
    );
};

export default BusinessPicker;
